import { ChangeEvent, useCallback, useEffect, useMemo, useRef } from "react";

import { Editor } from "@tiptap/core";
import { Node as ProsemirrorNode } from "@tiptap/pm/model";
import { ImageIcon } from "lucide-react";

import { getImageComponentImageFileMap } from "@/components/tiptap/extensions/custom-image";
import { uploadFirstImageAndInsertRemaining, useUploader } from "@/components/tiptap/helpers/use-file-upload";
import { cn } from "@/lib/utils";

export const CustomImageUploader = (props: {
  failedToLoadImage: boolean;
  editor: Editor;
  selected: boolean;
  loadImageFromFileSystem: (_: string) => void;
  setIsUploaded: (_: boolean) => void;
  node: ProsemirrorNode;
  updateAttributes: (_: Record<string, any>) => void;
  getPos: () => number;
}) => {
  const {
    selected,
    failedToLoadImage,
    editor,
    loadImageFromFileSystem,
    node,
    setIsUploaded,
    updateAttributes,
    getPos,
  } = props;
  // ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasTriggeredFilePickerRef = useRef(false);
  const imageEntityId = node.attrs.id ?? "";

  const imageComponentImageFileMap = useMemo(() => getImageComponentImageFileMap(editor), [editor]);

  const onUpload = useCallback(
    (url: string) => {
      if (url) {
        setIsUploaded(true);
        // Update the node view's src attribute post upload
        updateAttributes({ src: url });
        imageComponentImageFileMap?.delete(imageEntityId);

        const pos = getPos();
        // get current node
        const getCurrentSelection = editor.state.selection;
        const currentNode = editor.state.doc.nodeAt(getCurrentSelection.from);

        // only if the cursor is at the current image component, manipulate
        // the cursor position
        if (currentNode && currentNode.type.name === "imageComponent" && currentNode.attrs.src === url) {
          // control cursor position after upload
          const nextNode = editor.state.doc.nodeAt(pos + 1);

          if (nextNode && nextNode.type.name === "paragraph") {
            // If there is a paragraph node after the image component, move the focus to the next node
            editor.commands.setTextSelection(pos + 1);
          } else {
            // create a new paragraph after the image component post upload
            editor.commands.createParagraphNear();
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imageComponentImageFileMap, imageEntityId, updateAttributes, getPos]
  );
  // hooks
  const { uploading: isImageBeingUploaded, uploadFile } = useUploader({ onUpload, editor, loadImageFromFileSystem });

  // the meta data of the image component
  const meta = useMemo(
    () => imageComponentImageFileMap?.get(imageEntityId),
    [imageComponentImageFileMap, imageEntityId]
  );

  // after the image component is mounted we start the upload process based on
  // it's uploaded
  useEffect(() => {
    if (meta) {
      if (meta.event === "insert" && fileInputRef.current && !hasTriggeredFilePickerRef.current) {
        if (meta.hasOpenedFileInputOnce) return;
        fileInputRef.current.click();
        hasTriggeredFilePickerRef.current = true;
        imageComponentImageFileMap?.set(imageEntityId, { ...meta, hasOpenedFileInputOnce: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, uploadFile, imageComponentImageFileMap]);

  const onFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      const fileList = e.target.files;
      if (!fileList) {
        return;
      }
      await uploadFirstImageAndInsertRemaining(editor, fileList, getPos(), uploadFile);
    },
    [uploadFile, editor, getPos]
  );

  const getDisplayMessage = useCallback(() => {
    const isUploading = isImageBeingUploaded;
    if (failedToLoadImage) {
      return "Error loading image";
    }

    if (isUploading) {
      return "Uploading...";
    }

    return "Add an image";
  }, [failedToLoadImage, isImageBeingUploaded]);

  return (
    <div
      className={cn(
        "image-upload-component flex items-center justify-start gap-2 py-3 px-2 rounded-lg max-w-56 h-20 text-ash-400 hover:text-ash-200 bg-ash-900/10 hover:bg-ash-900/20 border border-dashed border-ash-800 transition-all duration-200 ease-in-out cursor-default",
        {
          "hover:text-ash-200 cursor-pointer": editor.isEditable,
          "text-ash-200 bg-ash-900/20 hover:bg-ash-900/30 hover:text-ash-200 border-ash-800/50": selected,
          "text-lava-500 cursor-default hover:text-lava-500": failedToLoadImage,
          "bg-lava-500/10 hover:bg-lava-500/10": failedToLoadImage && selected,
        }
      )}
      contentEditable={false}
      onClick={() => {
        if (!failedToLoadImage && editor.isEditable) {
          fileInputRef.current?.click();
        }
      }}
    >
      <ImageIcon className="size-4" />
      <div className="text-base font-medium">{getDisplayMessage()}</div>
      <input
        className="size-0 overflow-hidden"
        ref={fileInputRef}
        hidden
        type="file"
        accept=".jpg,.jpeg,.png,.webp"
        onChange={onFileChange}
      />
    </div>
  );
};
